import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ReactiveFormsModule, FormsModule, FormBuilder, FormControl } from '@angular/forms';
import { MatAnchor, MatIconButton, MatButton } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Params, Router, RouterLink } from '@angular/router';
import { BreadcrumbComponent } from '../../breadcrumb/breadcrumb.component';
import { Procedure } from '../../../models/procedures/ProcedureResponse';
import { Subscription, merge, startWith, tap, debounceTime, distinctUntilChanged } from 'rxjs';
import { Category } from '../../../models/categories/Category';
import { Etat } from '../../../models/etats/EtatBody';
import { Mode } from '../../../models/modes/Mode';
import { Perimetre } from '../../../models/perimetre/Perimetre';
import { CategoryService } from '../../../services/categories/categories.service';
import { EtatsService } from '../../../services/etats/etats.service';
import { ModesService } from '../../../services/modes/modes.service';
import { PerimetresService } from '../../../services/perimetres/perimetres.service';
import { ProceduresService } from '../../../services/procedures/procedures.service';
import { ProfilService } from '../../../services/profil/profil.service';
import { TypesService } from '../../../services/types/types.service';
import {Type} from '../../../models/types/Type';
import { UtilisateurService } from '../../../services/utilisateurs/utilisateur.service';
import { DeleteProcedureDialogComponent } from '../delete-procedure-dialog/delete-procedure-dialog.component';
import { JointjsReadonlyModalComponent } from '../jointjs-readonly-modal/jointjs-readonly-modal.component';

@Component({
  selector: 'app-liste-procedures-archivees',
  standalone: true,
  imports: [
    MatTableModule, MatIconModule, MatSortModule, MatPaginatorModule, MatInputModule,
    MatCardModule, MatDividerModule, MatSelectModule, MatDialogModule, CommonModule, MatCheckboxModule,
    ReactiveFormsModule, FormsModule, MatFormFieldModule, MatSnackBarModule, BreadcrumbComponent, MatAnchor, MatIconButton, MatButton, RouterLink, MatMenuTrigger, MatMenu, MatMenuItem
  ],
  templateUrl: './liste-procedures-archivees.component.html',
  styleUrl: './liste-procedures-archivees.component.scss'
})
export class ListeProceduresArchiveesComponent implements AfterViewInit, OnInit, OnDestroy{
  displayedColumns: string[] = ['mnemonique', 'version', 'titre', 'type', 'category', 'mode', 'dateArchivage', 'perimetre', 'actions'];
  dataSource = new MatTableDataSource<Procedure>();
  modes: Mode[] = [];
  categories: Category[] = [];
  types: Type[] = [];
  perimetres: Perimetre[] = [];
  etats: Etat[] = [];
  filterGeneral = '';
  filterMode = '';
  filterCategory = '';
  filterType = '';
  filterPerimetre = '';
  filterEtat = '';
  filtersActive = false;
  verticalPosition: MatSnackBarVerticalPosition = 'top';
  currentProfile: string | null = null;
  isFirstLoad: boolean = true;
  currentUserId: number | null = null;

  subscriptions: Subscription[] = [];

  @ViewChild(MatSort) sort?: MatSort;
  @ViewChild(MatPaginator) paginator?: MatPaginator;

  filter = this._formBuilder.group({
    general_filter: new FormControl('', []),
    mode: new FormControl('', []),
    category: new FormControl('', []),
    type: new FormControl('', []),
    perimetre: new FormControl('',[]),
    etat: new FormControl('', [])
});

  constructor(
    public dialog: MatDialog,
    private router: Router,
    private proceduresService: ProceduresService,
    private modeService: ModesService,
    private categoryService: CategoryService,
    private typeService: TypesService,
    private perimetresService: PerimetresService,
    private etatsService: EtatsService,
    private profilService: ProfilService,
    private utilisateurService: UtilisateurService,
    private _formBuilder: FormBuilder,
    public snackBar: MatSnackBar
  ) {}

  ngOnInit() {
    this.subscriptions.push(
      this.profilService.currentProfile$.subscribe(
        (profile) => {
          this.currentProfile = profile;
          console.log('Current profil : ', this.currentProfile);
          this.loadEtats();
        }
      )
    )
    this.subscriptions.push(
      this.utilisateurService.currentUser$.subscribe(userId => {
        this.currentUserId = userId;
      })
    )

    this.loadModes();
    this.loadCategories();
    this.loadTypes();
    this.loadPerimetres();
    this.setupFilterListeners();
  }

  ngAfterViewInit(): void {
    if (this.sort && this.paginator) {

      this.sort.active = 'updatedAt';
      this.sort.direction = 'desc';
      this.subscriptions.push(
        this.sort.sortChange.subscribe(() => (this.paginator!.pageIndex = 0))
      )

      this.subscriptions.push(
        merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          startWith(null),
          tap(() => this.loadProcedures())
          )
        .subscribe()
      )
    }
    else {
      this.loadProcedures();
    }

    this.subscriptions.push(
      this.filter.controls.general_filter.valueChanges.pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap((searchText) => {
          this.filterGeneral = searchText || '';
          this.paginator!.pageIndex = 0;
          this.loadProcedures();
        })
      ).subscribe()
    )
  }

  loadProcedures() {
    if (this.paginator && this.sort) {
      const archiveEtatId = this.etats.find(e => e.libelle === 'Archivé')?.id;

      const params: Params = {
        page: this.paginator.pageIndex + 1,
        limit: this.paginator.pageSize,
        search: this.filterGeneral,
        mode: this.filterMode,
        category: this.filterCategory,
        type: this.filterType,
        perimetre: this.filterPerimetre,
        etat: archiveEtatId
      };

      const adminProfiles = ['Administrateur OPUS', 'Chargé de mission', 'Direction Exploitation'];

      if (this.currentProfile && !adminProfiles.includes(this.currentProfile)) {
        const enApplicationEtatId = this.etats.find(e => e.libelle === 'En application')?.id;
        if (enApplicationEtatId) {
          params['etat'] = enApplicationEtatId;
        }
        params['listeUtilisateursIds'] = this.currentUserId;
        params['orderBy'] = 'mnemonique';
        params['direction'] = 'ASC';

      } else {
        if (this.sort.active && this.sort.direction !== '') {
          params['orderBy'] = this.sort.active;
          params['direction'] = this.sort.direction;
        }
      }

      this.subscriptions.push(
        this.proceduresService.getProcedures(params).subscribe({
          next: (procedures) => {
            this.dataSource.data = procedures.data;
            this.paginator!.length = procedures.total;
            this.isFirstLoad = false;
          },
          error: (error) => {
            console.error('Erreur lors de la récupération des procédures', error);
          }
        })
      )
    }
  }

  loadModes() {
    this.subscriptions.push(
      this.modeService.getModes().subscribe({
        next: (modes) => {
          this.modes = modes.data
        },
        error: (error) => {
          console.log('Erreur lors de la récupération des modes', error);
        }
      })
    )
  }

  loadCategories() {
    this.subscriptions.push(
      this.categoryService.getCategories().subscribe({
        next: (categories) => {
          this.categories = categories.data
        },
        error: (error) => {
          console.log('Erreur lors de la récupération des catégories', error);
        }
      })
    )
  }

  loadTypes() {
    this.subscriptions.push(
      this.typeService.getTypes().subscribe({
        next: (types) => {
          this.types = types.data
        },
        error: (error) => {
          console.log('Erreur lors de la récupération des types', error);
        }
      })
    )
  }

  loadPerimetres() {
    this.subscriptions.push(
      this.perimetresService.getPerimetres().subscribe({
        next: (perimetres) => {
          this.perimetres = perimetres.data
        },
        error: (error) => {
          console.log('Erreur lors de la récupération des périmètres', error);
        }
      })
    )
  }

  loadEtats() {
    this.subscriptions.push(
      this.etatsService.getEtats().subscribe({
        next: (etats) => {
          this.etats = etats.data;
          this.loadProcedures();
        },
        error: (error) => {
          console.log('Erreur lors de la récupération des états', error);
        }
      })
    )
  }

  applyFilter(value: string, filter: string): void {
    if(filter === 'mode'){
      this.filterMode = value;
      this.filter.controls.mode.setValue(value);
    } else if(filter === 'category') {
        this.filterCategory = value;
        this.filter.controls.category.setValue(value);
    } else if(filter === 'type') {
        this.filterType = value;
        this.filter.controls.type.setValue(value);
    } else if(filter === 'perimetre') {
      this.filterPerimetre = value;
      this.filter.controls.perimetre.setValue(value);
    }

    this.paginator!.pageIndex = 0;
    this.loadProcedures();
  }

  clearGeneralFilter() {
    this.filter.controls['general_filter'].setValue('');
    this.loadProcedures();
  }

  resetFilters() {
    this.filter.controls['mode'].setValue('');
    this.filterMode = '';
    this.filter.controls['category'].setValue('');
    this.filterCategory = '';
    this.filter.controls['type'].setValue('');
    this.filterType = '';
    this.filter.controls['perimetre'].setValue('');
    this.filterPerimetre = '';
    this.filter.controls['general_filter'].setValue('');
    this.filterGeneral = '';
    this.loadProcedures();
  }


  setupFilterListeners() {
    this.subscriptions.push(this.filter.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(() => {
      const generalFilterActive = this.filter.controls['general_filter'].value !== '';
      const modeFilterActive = this.filter.controls['mode'].value !== '';
      const categoryFilterActive = this.filter.controls['category'].value !== '';
      const typeFilterActive = this.filter.controls['type'].value !== '';
      const perimetreFilterActive = this.filter.controls['perimetre'].value !== '';
      this.filtersActive = generalFilterActive || modeFilterActive || categoryFilterActive || typeFilterActive
                            || perimetreFilterActive;
    }))
  }

  openEditDialog(id: number): void {
      this.router.navigate(['procedures/modifier-procedure', id], { queryParams: { readOnly: true } });

  }

  openReadOnlyLogigrammeDialog(procedure: any) {
    if(procedure && procedure.titre &&  procedure.logigramme) {
      this.dialog.open(JointjsReadonlyModalComponent, {
        maxWidth: '100vw',
        maxHeight: '100vh',
        height: '100%',
        width: '100%',
        data: { logigramme: procedure.logigramme, procedureTitre: procedure.titre }
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(x => x.unsubscribe());
  }

}
